import { useEffect, useState } from "react";
import useLazyLoading from "../../common/hooks/loader";
import Loader from "../../components/loader";
import { Button, FormHelperText, Grid, IconButton, InputAdornment, InputLabel, OutlinedInput, Stack } from "@mui/material";
import { useFormik } from "formik";
import { IUserTokenDataDto } from "../../interfaces/user/IUserTokenDataDto";
import { tryGetUserLoggedIn, tryUpdateUser } from "../../services/user/user.service";
import { useNotification } from "../../components/notification/useNotification";
import extractErrorMessage from "../../common/utils/errorMessages";
import { IUserDto } from "../../interfaces/user/IUserDto";
import { initial } from "lodash";
import { EditAccountValidationSchema } from "./edit-account.validation";
import { useNavigate } from "react-router-dom";
import { getBaseURLWithAuth } from "../../common/utils/session";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";


export default function EditAccountForm() {
    const [loading, setLoading] = useState(false);
    const showLoading = useLazyLoading(loading);
    const [user, setUser] = useState<IUserDto>();
    const { displayNotification } = useNotification();
    const [confirmPassword, setConfirmPassword] = useState<string>();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const navigate = useNavigate();

    const initialValues: IUserDto = {
        id: '',
        name: '',
        password: '',
        email: '',
        isActive: user?.isActive!,
        userName: ''
    }

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: EditAccountValidationSchema,
        onSubmit: async (values, { setSubmitting }) => {

            if (values.password) {
                if (values.password != confirmPassword) {
                    displayNotification({ type: 'error', message: 'As passwords não coincidem' });
                    return;
                }
            }

            try {
                setLoading(true);

                const userToUpdate: IUserDto = {
                    id: user!.id,
                    email: user!.email,
                    userName: user!.userName,
                    isActive: user!.isActive,
                    name: values.name,
                    password: values.password
                }


                var response = await tryUpdateUser(userToUpdate);

                if (response?.status === 200) {
                    displayNotification({ type: 'success', message: 'Utilizador actualizado com sucesso' });
                    navigate(getBaseURLWithAuth() + 'dashboard');
                }
            }
            catch (error) {
                displayNotification({ type: 'error', message: extractErrorMessage(error) });
            }
            finally {
                setLoading(false);
            }
        }
    })

    async function getUserLoggedIn() {
        try {
            var response = await tryGetUserLoggedIn();

            if (response?.status === 200) {
                setUser(response.data);
                updateFormValues(response.data);
            }
        }
        catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) });
        }
    }

    useEffect(() => {
        setLoading(true);

        getUserLoggedIn();

        setLoading(false);
    }, []);

    function updateFormValues(userDto: IUserDto) {
        formik.setFieldValue('name', userDto.name);
    }

    return (
        <>
            {showLoading ? <Loader /> : false}
            <form noValidate onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                    <Grid sx={{ flex: 1 }}>
                        <Stack spacing={1} margin={3}>
                            <InputLabel htmlFor="userName" required sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                Nome
                            </InputLabel>
                            <OutlinedInput
                                id="userName"
                                type="text"
                                onBlur={formik.handleBlur}
                                value={formik.values.name}
                                onChange={(event) => formik.setFieldValue('name', event.target.value)}
                                error={Boolean(formik.touched.name && formik.errors.name)}
                            />

                            {formik.touched.name && formik.errors.name && (
                                <FormHelperText error id="helper-text-userName-error">
                                    {formik.errors.name}    
                                </FormHelperText>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
                <Grid container spacing={3}>
                    <Grid sx={{ flex: 1 }}>
                        <Stack spacing={1} margin={3}>
                            <InputLabel htmlFor="password" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                Password
                            </InputLabel>
                            <OutlinedInput
                                id="password"
                                type={ showPassword ? "text" : "password" }
                                onBlur={formik.handleBlur}
                                value={formik.values.password}
                                onChange={(event) => formik.setFieldValue('password', event.target.value)}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            size="large"
                                        >
                                            {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                        </IconButton>
                                    </InputAdornment>
                                }
                            />
                        </Stack>
                    </Grid>
                    <Grid sx={{ flex: 1 }}>
                        <Stack spacing={1} margin={3}>
                            <InputLabel htmlFor="confirm-password" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                Confirmar Password
                            </InputLabel>
                            <OutlinedInput
                                id="confirm-password"
                                type= { showConfirmPassword ? "text" : "password" }
                                onBlur={formik.handleBlur}
                                value={confirmPassword}
                                onChange={(event) => setConfirmPassword(event.target.value)}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                            size="large"
                                        >
                                            {showConfirmPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                        </IconButton>
                                    </InputAdornment>
                                }
                            />
                        </Stack>
                    </Grid>
                </Grid>
                <Grid margin={3}>
                    <Button
                        disableElevation
                        disabled={formik.isSubmitting}
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                        color="primary"
                    >
                        Confirmar
                    </Button>
                </Grid>
            </form>
        </>
    )
}